import {
  FaBook,
  FaFile,
  FaFileInvoice,
  FaInfo,
  FaRegCalendar,
  FaStar,
} from "react-icons/fa6";
import { GrAnnounce } from "react-icons/gr";
import TabBar, { Tab } from "../../../../../../components/TabBar";
import { useEffect, useState } from "react";
import useDocumentsByUserOrganizationId from "../../hooks/useDocumentsByUserOrganizationId";
import {
  DocumentFragment,
  DocumentType,
} from "../../../../../../__generated__/graphql";
import { motion } from "motion/react";

enum CollaboratorTabValue {
  Information = "information",
  Planning = "planning",
  Events = "events",
  Communication = "communication",
  Declarations = "declarations",
  Gains = "gains",
  JourAJour = "jour-a-jour",
  Scenarios = "scenarios",
  Documents = "documents",
}

const COLLABORATOR_TABS: Tab[] = [
  {
    label: "Informations",
    value: CollaboratorTabValue.Information,
    icon: <FaInfo className="w-4 h-4" />,
  },
  {
    label: "Planning",
    value: CollaboratorTabValue.Planning,
    icon: <FaRegCalendar className="w-4 h-4" />,
  },
  {
    label: "Évènements",
    value: CollaboratorTabValue.Events,
    icon: <FaStar className="w-4 h-4" />,
  },
];

const TABS_BY_DOCUMENT_TYPE: Record<DocumentType, Tab> = {
  [DocumentType.Communication]: {
    label: "Communication",
    value: CollaboratorTabValue.Communication,
    icon: <GrAnnounce className="w-4 h-4" />,
  },
  [DocumentType.DeclarationSacd]: {
    label: "Déclarations",
    value: CollaboratorTabValue.Declarations,
    icon: <FaFileInvoice className="w-4 h-4" />,
  },
  [DocumentType.FicheDePaie]: {
    label: "Déclarations",
    value: CollaboratorTabValue.Declarations,
    icon: <FaFileInvoice className="w-4 h-4" />,
  },
  [DocumentType.Bordereau]: {
    label: "Gains",
    value: CollaboratorTabValue.Gains,
    icon: <FaFileInvoice className="w-4 h-4" />,
  },
  [DocumentType.JourAJour]: {
    label: "Jour à jours",
    value: CollaboratorTabValue.JourAJour,
    icon: <FaFileInvoice className="w-4 h-4" />,
  },
  [DocumentType.Scenario]: {
    label: "Scénarios",
    value: CollaboratorTabValue.Scenarios,
    icon: <FaBook className="w-4 h-4" />,
  },
  [DocumentType.Other]: {
    label: "Autres",
    value: CollaboratorTabValue.Documents,
    icon: <FaFile className="w-4 h-4" />,
  },
};

interface CollaboratorTabBarProps {
  onTabChange: (tab: Tab) => void;
  userOrganizationId?: string;
}

export default function CollaboratorTabBar({
  onTabChange,
  userOrganizationId,
}: CollaboratorTabBarProps) {
  const [tabs, setTabs] = useState<Tab[]>(COLLABORATOR_TABS);

  const { data: documentsData, loading: documentsLoading } =
    useDocumentsByUserOrganizationId(userOrganizationId);

  const [selectedTab, setSelectedTab] = useState<Tab>(COLLABORATOR_TABS[0]);

  useEffect(() => {
    if (documentsData) {
      // If there are documents from a type, add the tab for that type
      // = flexible colloborator tabs
      const newTabs = [...COLLABORATOR_TABS];

      Object.values(DocumentType).forEach((type) => {
        const hasType = hasDocumentsFromType(
          type,
          documentsData.documentsByUserOrganizationId ?? []
        );
        if (hasType) {
          const isAlreadyInTabs = newTabs.some(
            (tab) => tab.value === TABS_BY_DOCUMENT_TYPE[type].value
          );

          if (!isAlreadyInTabs) {
            newTabs.push(TABS_BY_DOCUMENT_TYPE[type]);
          }
        }
      });

      // If Other category is not empty, put it at the end of the tabs
      const otherTab = TABS_BY_DOCUMENT_TYPE[DocumentType.Other];
      const hasOtherTab = newTabs.some(
        (tab) => tab.value === CollaboratorTabValue.Documents
      );

      if (hasOtherTab) {
        setTabs([
          ...newTabs.filter(
            (tab) => tab.value !== CollaboratorTabValue.Documents
          ),
          otherTab,
        ]);
      } else {
        setTabs(newTabs);
      }
    }
  }, [documentsData]);

  const hasDocumentsFromType = (
    type: DocumentType,
    documents: DocumentFragment[]
  ) => {
    return documents.some((document) => document.type === type);
  };

  if (documentsLoading)
    return <div className="h-10 w-1/2 rounded-md bg-input/50" />;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <TabBar
        tabs={tabs}
        activeTab={selectedTab}
        setActiveTab={(tab) => {
          setSelectedTab(tab);
          onTabChange(tab);
        }}
      />
    </motion.div>
  );
}
